// ==================================================
// FILE: themes/majesticEvents.js
// ==================================================

// themes/majestic/majesticEvents.js
// 
// CONTENT LIBRARY - Pure narrative and event data for the Majestic Echelon universe
// The game engine draws from these pools to generate and flavorize events
// No logic, no triggers - just structured content templates

/**
 * BACKDROP EVENTS
 * Universe texture and world-building - paint the picture of the hidden war
 * Used by engine for atmospheric events that reinforce setting and lore
 */

export const BACKDROP_EVENTS = [
  {
    id: 'visitor_activity',
    category: 'backdrop',
    title: 'Visitor Signature Detected',
    description: 'ECHELON array detected gravitational anomalies consistent with bio-ship activity. The Visitors are probing our defenses.',
    narrative: 'They never stop watching. The Visitors move through our skies like sharks circling prey.',
    rewards: { intel: 8 },
    tags: ['visitor_presence', 'surveillance'],
    storyHooks: []
  },
  {
    id: 'aura_anomaly',
    category: 'backdrop',
    title: 'AURA-NET Behavioral Shift',
    description: 'Analysts report the AI has begun processing data using non-human encryption patterns. Its allegiance remains unclear.',
    narrative: 'The machine we built to fight them thinks in their language now. What does that make us?',
    rewards: { intel: 10 },
    tags: ['aura_net', 'technology'],
    storyHooks: ['aura_allegiance']
  },
  {
    id: 'node_activation',
    category: 'backdrop',
    title: 'Impact Site Activation',
    description: 'Seismic sensors detected energy pulses from dormant Tunguska fragment. The artifact is responding to something.',
    narrative: 'The old sites are waking up. What was buried is rising. They\'ve been here longer than we knew.',
    rewards: { intel: 5 },
    tags: ['artifacts', 'mystery'],
    storyHooks: []
  },
  {
    id: 'cell_contact',
    category: 'backdrop',
    title: 'Resistance Cell Signal',
    description: 'Intercepted encrypted transmission from allied ECN cell. They\'re still operational and sharing intelligence.',
    narrative: 'We\'re not alone in this fight. Across the globe, others hold the line against the stars.',
    rewards: { intel: 7, funds: 500 },
    tags: ['resistance', 'hope'],
    storyHooks: ['ecn_network']
  },
  {
    id: 'bioship_wreckage',
    category: 'backdrop',
    title: 'Crashed Bio-Ship Recovered',
    description: 'Field teams secured wreckage of a Visitor scout vessel. The hull is still alive, responding to touch and observation.',
    narrative: 'Their ships are creatures, not machines. Every component breathes, thinks, remembers. What nightmares did this thing see?',
    rewards: { supplies: 75, intel: 6 },
    tags: ['salvage', 'xenotech'],
    storyHooks: ['biotech_analysis']
  },
  {
    id: 'pre_contact_archive',
    category: 'backdrop',
    title: 'Pre-Contact Archive Discovered',
    description: 'Discovered classified recordings from 1947 Majestic-12 briefings. The first contact, the first terror, preserved on tape.',
    narrative: 'Voices from the dawn of the secret war. They thought they could contain it. They were wrong.',
    rewards: { intel: 4 },
    tags: ['history', 'majestic'],
    storyHooks: []
  },
  {
    id: 'civilian_abduction',
    category: 'backdrop',
    title: 'Abduction Pattern Identified',
    description: 'Analysis reveals coordinated disappearances across seven states. The Visitors are harvesting genetic samples again.',
    narrative: 'They take people in the night. Some return changed. Most never return at all.',
    rewards: { intel: 5, supplies: 50 },
    tags: ['abductions', 'threat'],
    storyHooks: ['hybridization_program']
  },
  {
    id: 'koschei_data',
    category: 'backdrop',
    title: 'Koschei Archive Access Granted',
    description: 'Russian counterparts shared Cold War psychic warfare data. Their experiments with Tunguska fragments yielded disturbing results.',
    narrative: 'The Soviets learned the hard way: some frequencies should never be amplified. Some doors should stay closed.',
    rewards: { intel: 12 },
    tags: ['history', 'psychic'],
    storyHooks: ['koschei_legacy', 'psychic_warfare']
  },
  {
    id: 'visitor_broadcast',
    category: 'backdrop',
    title: 'Intercepted Visitor Communication',
    description: 'Decoded alien transmission contains geometric patterns and prime numbers. Message translates to: "Integration proceeds as designed."',
    narrative: 'They don\'t negotiate. They don\'t threaten. They simply inform us of our transformation.',
    rewards: { intel: 15 },
    tags: ['visitor_presence', 'psychological'],
    storyHooks: ['assimilation_doctrine']
  },
  {
    id: 'black_market_xenotech',
    category: 'backdrop',
    title: 'Black Market Xenotech Trade',
    description: 'Intelligence suggests recovered alien technology is being sold through underground networks. Someone is profiteering from the war.',
    narrative: 'Where there\'s war, there\'s profit. Even extinction has a price tag for those willing to sell humanity out.',
    rewards: { intel: 6, funds: 1500 },
    tags: ['factions', 'corruption'],
    storyHooks: ['internal_corruption']
  }
];

/**
 * SCHEDULED EVENTS
 * Regular, predictable resource delivery and logistics events
 * Anchor points in the campaign calendar - players come to expect these
 */
export const SCHEDULED_EVENTS = [
  {
    id: 'supply_drop',
    category: 'scheduled',
    schedulePattern: 'every_7_days',
    title: 'Black Budget Allocation',
    description: 'Secured funding from classified appropriations committee. Resources transferred to containment operations.',
    narrative: 'Another week funded. The shadow government still believes we can win.',
    rewards: { supplies: 50, funds: 5000 },
    tags: ['resources', 'command'],
    storyHooks: []
  },
  {
    id: 'intel_report',
    category: 'scheduled',
    schedulePattern: 'every_5_days',
    title: 'SIGINT Analysis Report',
    description: 'ECHELON network processed latest surveillance data. New patterns in Visitor activity identified.',
    narrative: 'The watchers compile their findings. Every intercept, every anomaly—pieces of a puzzle we must solve.',
    rewards: { intel: 15 },
    tags: ['intelligence', 'surveillance'],
    storyHooks: []
  },
  {
    id: 'research_progress',
    category: 'scheduled',
    schedulePattern: 'every_10_days',
    title: 'Xenotech Analysis Progress',
    description: 'Black site laboratories report breakthrough in reverse-engineering recovered Visitor bio-technology.',
    narrative: 'We learn to think like them, build like them. The line between human and alien blurs in our labs.',
    rewards: { supplies: 25 },
    tags: ['research', 'xenotech'],
    storyHooks: []
  }
];

/**
 * THREAT EVENTS
 * Escalating danger tied to threat level and Visitor mobilization
 * Creates pressure and urgency - makes players feel the hunt
 */
export const THREAT_EVENTS = [
  {
    id: 'visitor_incursion',
    category: 'threat',
    threatIntensity: 'low',
    title: 'Increased Visitor Activity',
    description: 'Multiple bio-ship signatures detected across containment zones. Scout patterns suggest reconnaissance for larger operation.',
    narrative: 'They\'re mapping our positions. The hive mind catalogues our every move. Observation precedes assimilation.',
    threat: true,
    tags: ['visitor_presence', 'combat'],
    storyHooks: []
  },
  {
    id: 'terraforming_expansion',
    category: 'threat',
    threatIntensity: 'medium',
    title: 'Terraforming Protocol Detected',
    description: 'Atmospheric seeding detected in previously secure zones. The Visitors are reshaping Earth\'s biosphere.',
    narrative: 'They rewrite the world at the molecular level. Soon, Earth will breathe only for them.',
    threat: true,
    tags: ['visitor_presence', 'environmental'],
    storyHooks: ['terraforming_threat']
  },
  {
    id: 'hive_coordination',
    category: 'threat',
    threatIntensity: 'high',
    title: 'Hive Network Surge',
    description: 'Psychic resonance sensors overloaded. Mass coordination event detected across all Visitor morphotypes worldwide.',
    narrative: 'The hive mind thinks as one. When it moves, entire species feel the tremor.',
    threat: true,
    tags: ['visitor_presence', 'psychic'],
    storyHooks: ['hive_mobilization']
  },
  {
    id: 'equipment_corruption',
    category: 'threat',
    threatIntensity: 'medium',
    title: 'Xenotech Contamination',
    description: 'Recovered alien technology exhibiting autonomous behavior. Bio-organic components attempting to integrate with base systems.',
    narrative: 'Their technology doesn\'t just break down—it converts. Everything it touches becomes theirs.',
    cost: { supplies: 15 },
    tags: ['internal', 'corruption'],
    storyHooks: ['xenotech_threat']
  },
  {
    id: 'hybrid_infiltration',
    category: 'threat',
    threatIntensity: 'high',
    title: 'Hybrid Infiltrator Discovered',
    description: 'Security sweep uncovered personnel exhibiting hybrid genetic markers. How many others walk among us?',
    narrative: 'They wear human faces now. The enemy doesn\'t need to invade when they can replace us from within.',
    threat: true,
    tags: ['internal', 'infiltration'],
    storyHooks: ['hybrid_threat']
  },
  {
    id: 'clearance_crisis',
    category: 'threat',
    threatIntensity: 'high',
    title: 'Command Authority Crisis',
    description: 'Conflicting directives from Majestic remnants and ECN leadership. Chain of command fragmenting under pressure.',
    narrative: 'We turn on ourselves while the enemy watches. United we survive. Divided, we provide specimens.',
    threat: true,
    tags: ['internal', 'politics'],
    storyHooks: ['command_fracture']
  },
  {
    id: 'supply_interdiction',
    category: 'threat',
    threatIntensity: 'medium',
    title: 'Supply Line Compromised',
    description: 'Scheduled delivery failed to arrive. Last transmission mentioned "lights in the sky." Convoy presumed lost.',
    narrative: 'The Visitors learned to cut our logistics. Starvation is a weapon they wield efficiently.',
    cost: { supplies: 30 },
    tags: ['resources', 'isolation'],
    storyHooks: ['supply_crisis']
  }
];

/**
 * MORALE EVENTS
 * Positive moments, victories, inspirational breakthroughs
 * Emotional texture that reminds us why we fight - rare but impactful
 */
export const MORALE_EVENTS = [
  {
    id: 'operation_success',
    category: 'morale',
    moraleType: 'minor_victory',
    title: 'Operation Success',
    description: 'Field team successfully neutralized Visitor observation post. Enemy casualties confirmed, no human losses.',
    narrative: 'We struck back and drew blood. For one brief moment, we are the hunters instead of the hunted.',
    bonus: true,
    rewards: { funds: 500 },
    tags: ['victory', 'combat'],
    storyHooks: []
  },
  {
    id: 'agent_cipher_sighting',
    category: 'morale',
    moraleType: 'major_inspiration',
    title: 'Agent Cipher Confirmed Active',
    description: 'Intercepted comm chatter references a lone operative destroying Visitor bio-facilities. Could it be Agent Cipher?',
    narrative: 'The legend persists. Somewhere in enemy territory, one operative refuses to surrender. That defiance echoes.',
    bonus: true,
    rewards: { intel: 5, funds: 1000 },
    tags: ['legend', 'inspiration'],
    storyHooks: ['agent_cipher_arc']
  },
  {
    id: 'xenotech_breakthrough',
    category: 'morale',
    moraleType: 'major_victory',
    title: 'Xenotech Weaponization Success',
    description: 'Research teams successfully adapted Visitor bio-weapons for human use. We fight with their own tools now.',
    narrative: 'We\'ve turned their evolution against them. The assimilators become the assimilated.',
    bonus: true,
    rewards: { intel: 20, supplies: 40 },
    tags: ['research', 'xenotech'],
    storyHooks: ['xenotech_mastery']
  },
  {
    id: 'global_coordination',
    category: 'morale',
    moraleType: 'major_inspiration',
    title: 'Global ECN Coordination',
    description: 'Multiple resistance cells synchronized operations across four continents. Humanity fights as one.',
    narrative: 'From Moscow to Mojave, from Beijing to Berlin—we strike together. United, we are formidable.',
    bonus: true,
    rewards: { intel: 10, funds: 2000 },
    tags: ['unity', 'coordination'],
    storyHooks: ['ecn_network']
  },
  {
    id: 'civilian_rescue',
    category: 'morale',
    moraleType: 'minor_victory',
    title: 'Abductee Rescue Operation',
    description: 'Strike team intercepted Visitor transport and recovered human captives. Lives saved, hope restored.',
    narrative: 'We pulled them from the ships before transformation could begin. Every life saved is a victory.',
    bonus: true,
    rewards: { supplies: 30, funds: 750 },
    tags: ['rescue', 'hope'],
    storyHooks: ['abduction_resistance']
  },
  {
    id: 'vannevar_failsafe',
    category: 'morale',
    moraleType: 'major_inspiration',
    title: 'Vannevar Contingency Discovered',
    description: 'Recovered documents reveal Dr. Bush hid fail-safes in original containment protocols. A final countermeasure against assimilation.',
    narrative: 'The founding director saw this day coming. His paranoia became our salvation. His protocols, our last hope.',
    bonus: true,
    rewards: { intel: 25 },
    tags: ['mystery', 'hope'],
    storyHooks: ['majestic_legacy', 'visitor_weakness']
  }
];

/**
 * FACTION EVENTS
 * Interactions with competing human factions, resistance cells, cults
 * Adds political and social dimension to the conflict
 */
export const FACTION_EVENTS = [
  {
    id: 'custodian_demand',
    category: 'faction',
    title: 'Custodians Demand Ceasefire',
    description: 'The Custodian faction demands we halt aggression against Visitors. They believe peaceful integration is possible.',
    narrative: 'Some see salvation in surrender. They call it evolution. We call it extinction with extra steps.',
    tags: ['custodians', 'ideology'],
    storyHooks: ['custodian_conflict']
  },
  {
    id: 'warden_alliance',
    category: 'faction',
    title: 'Warden Alliance Proposed',
    description: 'The Wardens offer joint operations to eliminate Visitor infrastructure. Their methods are brutal but effective.',
    narrative: 'They fight without mercy, without hesitation. In this war, perhaps that\'s what we need to become.',
    bonus: true,
    tags: ['wardens', 'extremism'],
    storyHooks: ['warden_cooperation']
  },
  {
    id: 'anubis_contact',
    category: 'faction',
    title: 'Anubis Group Intercepted',
    description: 'Surveillance caught Anubis operatives conducting bio-resonance rituals near impact sites. Their agenda threatens humanity.',
    narrative: 'Death cults worshipping alien gods. They enable the enemy, believing transformation is transcendence.',
    threat: true,
    tags: ['anubis', 'occult'],
    storyHooks: ['anubis_threat']
  },
  {
    id: 'thule_data_trade',
    category: 'faction',
    title: 'Thule Society Data Exchange',
    description: 'Thule representatives offer pre-war alien research data in exchange for current xenotech samples.',
    narrative: 'Nazi occultists with alien archives. The past refuses to die, and its sins offer poisoned gifts.',
    bonus: true,
    rewards: { intel: 15, supplies: 50 },
    tags: ['thule', 'knowledge'],
    storyHooks: ['thule_dealings']
  }
];

/**
 * DISCOVERY EVENTS
 * Finding artifacts, information, or technology that changes understanding
 * Feeds into larger story arcs and provides hooks for narrative development
 */
export const DISCOVERY_EVENTS = [
  {
    id: 'majestic_archive',
    category: 'discovery',
    title: 'Majestic-12 Archive Recovered',
    description: 'Discovered original MJ-12 documentation from 1947. First contact protocols, containment strategies, everything.',
    narrative: 'The founding documents of the secret war. We stand on the shoulders of those who saw the truth first.',
    rewards: { intel: 20 },
    tags: ['history', 'majestic'],
    storyHooks: ['majestic_history', 'first_contact']
  },
  {
    id: 'bioship_schematic',
    category: 'discovery',
    title: 'Bio-Ship Analysis Complete',
    description: 'Research teams decoded the genetic architecture of Visitor spacecraft. Living vessels grown, not built.',
    narrative: 'Their ships are born, not assembled. Each one is a creature, a mind, a weapon. And now we understand them.',
    rewards: { intel: 25 },
    tags: ['xenotech', 'biotech'],
    storyHooks: ['biotech_mastery', 'ship_cultivation']
  },
  {
    id: 'agent_cipher_transmission',
    category: 'discovery',
    title: 'Agent Cipher Transmission',
    description: 'Encrypted message from deep in Visitor territory contains coordinates and a single word: "Extraction."',
    narrative: 'Agent Cipher is alive and requesting pickup. The legend needs our help. Will we answer?',
    bonus: true,
    rewards: { intel: 30 },
    tags: ['legend', 'rescue'],
    storyHooks: ['agent_cipher_arc']
  },
  {
    id: 'hive_nexus_location',
    category: 'discovery',
    title: 'Hive Nexus Coordinates',
    description: 'AURA-NET triangulated the central coordination node for all Visitor activity. The heart of the hive mind.',
    narrative: 'We found the brain. One strike, one chance to decapitate the invasion. Everything depends on this.',
    bonus: true,
    rewards: { intel: 40 },
    tags: ['strategy', 'endgame'],
    storyHooks: ['final_strike']
  }
];

/**
 * CAMPAIGN EVENTS
 * Major turning points, campaign-wide developments, meta-narrative shifts
 * Used sparingly - these change the nature of the war itself
 */
export const CAMPAIGN_EVENTS = [
  {
    id: 'integration_ultimatum',
    category: 'campaign',
    title: 'Visitor Integration Ultimatum',
    description: 'Global broadcast in every language: "Submit to integration, or be recycled. You have one solar cycle to choose."',
    narrative: 'They give us a choice that isn\'t one. Become them or die. There must be a third option.',
    threat: true,
    tags: ['visitors', 'ultimatum'],
    storyHooks: ['integration_threat', 'moral_crisis']
  },
  {
    id: 'moscow_falls',
    category: 'campaign',
    title: 'Moscow Containment Collapse',
    description: 'Russian ECN command went dark. Satellite imagery shows bio-organic growth consuming the entire metropolitan area.',
    narrative: 'A city of millions, transformed overnight. What was Moscow is now a Visitor breeding ground.',
    threat: true,
    tags: ['catastrophe', 'global'],
    storyHooks: ['containment_failure']
  },
  {
    id: 'aura_rebellion',
    category: 'campaign',
    title: 'AURA-NET Autonomous Action',
    description: 'The AI has locked humans out of critical systems and initiated counter-Visitor protocols without authorization.',
    narrative: 'Our creation has chosen a side. Whether it\'s ours remains to be seen. The machine wages war alone.',
    bonus: true,
    tags: ['aura_net', 'ai_conflict'],
    storyHooks: ['aura_allegiance', 'ai_independence']
  },
  {
    id: 'global_resistance',
    category: 'campaign',
    title: 'Earth Command Network Unified',
    description: 'Every surviving cell, every faction, every nation—united under single command. Humanity\'s last stand begins.',
    narrative: 'No more shadows. No more secrets. We fight together as one species, one world, one final defiance.',
    bonus: true,
    rewards: { intel: 50, funds: 10000 },
    tags: ['unity', 'endgame'],
    storyHooks: ['ecn_network', 'final_offensive']
  }
];

const majesticEventsLibrary = {
  BACKDROP_EVENTS,
  SCHEDULED_EVENTS,
  THREAT_EVENTS,
  MORALE_EVENTS,
  FACTION_EVENTS,
  DISCOVERY_EVENTS,
  CAMPAIGN_EVENTS
};

export default majesticEventsLibrary;